/**
 * Tank Shape Definitions — 11 unique geometric silhouettes for tank identity.
 *
 * Two categories:
 * - Vertex-array shapes: work with Graphics.poly(verts)
 * - Draw-function shapes: use Graphics path API for curves
 *
 * drawTankShape() is the universal entry point used by body rendering,
 * flash overlay, DI/burst ghosts, and menu previews.
 *
 * Rendering: 3-pass neon wireframe strokes (no fills).
 */

import type { Graphics } from 'pixi.js';

// ─── Color Utility ───────────────────────────────────────────────────────────

/** Lighten a hex color by mixing toward white */
export function lighten(color: number, amount: number): number {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  return (
    (Math.min(255, Math.round(r + (255 - r) * amount)) << 16) |
    (Math.min(255, Math.round(g + (255 - g) * amount)) << 8) |
    Math.min(255, Math.round(b + (255 - b) * amount))
  );
}

// ─── 3-Pass Neon Stroke ─────────────────────────────────────────────────────

interface NeonPass {
  width: number;
  colorShift: number;  // lighten amount (0 = base color)
  alpha: number;
}

const BODY_PASSES: NeonPass[] = [
  { width: 6,   colorShift: 0,    alpha: 0.55 },
  { width: 3.5, colorShift: 0.2,  alpha: 0.85 },
  { width: 1.5, colorShift: 0.45, alpha: 1.0 },
];

// ─── Shape Registry ──────────────────────────────────────────────────────────

type ShapeType = 'polygon' | 'draw';

interface TankShapeDef {
  type: ShapeType;
  /** For polygon shapes: returns flat [x,y,x,y,...] vertex array centered at origin */
  vertices?: (radius: number) => number[];
  /** For draw shapes: draws directly onto Graphics centered at origin */
  draw?: (gfx: Graphics, radius: number) => void;
}

const SHAPE_REGISTRY: Record<string, TankShapeDef> = {};

function registerShape(tankId: string, def: TankShapeDef): void {
  SHAPE_REGISTRY[tankId] = def;
}

// ─── Mortar — circle (32-segment polygon approximation) ──────────────────────

registerShape('mortar', {
  type: 'polygon',
  vertices(radius: number): number[] {
    const pts: number[] = [];
    const segments = 32;
    for (let i = 0; i < segments; i++) {
      const a = (i * Math.PI * 2) / segments;
      pts.push(Math.cos(a) * radius, Math.sin(a) * radius);
    }
    return pts;
  },
});

// ─── Duelist — equilateral triangle (point up at rotation=0) ─────────────────

registerShape('duelist', {
  type: 'polygon',
  vertices(radius: number): number[] {
    const pts: number[] = [];
    for (let i = 0; i < 3; i++) {
      // -PI/2 offset so vertex 0 points up
      const a = (i * Math.PI * 2) / 3 - Math.PI / 2;
      pts.push(Math.cos(a) * radius, Math.sin(a) * radius);
    }
    return pts;
  },
});

// ─── Terraformer — hexagon (6-sided, same as legacy) ─────────────────────────

registerShape('terraformer', {
  type: 'polygon',
  vertices(radius: number): number[] {
    const pts: number[] = [];
    for (let i = 0; i < 6; i++) {
      const a = (i * Math.PI * 2) / 6;
      pts.push(Math.cos(a) * radius, Math.sin(a) * radius);
    }
    return pts;
  },
});

// ─── Bouncer — square (4 vertices: TL/TR/BR/BL) ────────────────────────────

registerShape('bouncer', {
  type: 'polygon',
  vertices(radius: number): number[] {
    return [
      -radius, -radius,  // top-left
      radius, -radius,   // top-right
      radius, radius,    // bottom-right
      -radius, radius,   // bottom-left
    ];
  },
});

// ─── Scatter — irregular star (6 spikes, pre-designed uneven lengths) ────────

registerShape('scatter', {
  type: 'polygon',
  vertices(radius: number): number[] {
    const pts: number[] = [];
    const spikes = 6;
    // Per-spike jitter multipliers — pre-designed for recognizable silhouette
    const outerMults = [1.0, 0.85, 1.1, 0.9, 1.05, 0.8];
    const innerMult = 0.45;
    for (let i = 0; i < spikes; i++) {
      // Outer spike
      const outerAngle = (i * Math.PI * 2) / spikes - Math.PI / 2;
      const outerR = radius * outerMults[i];
      pts.push(Math.cos(outerAngle) * outerR, Math.sin(outerAngle) * outerR);
      // Inner notch
      const innerAngle = ((i + 0.5) * Math.PI * 2) / spikes - Math.PI / 2;
      const innerR = radius * innerMult;
      pts.push(Math.cos(innerAngle) * innerR, Math.sin(innerAngle) * innerR);
    }
    return pts;
  },
});

// ─── Launcher — oblique cylinder silhouette (leaning cannon) ────────────────

registerShape('launcher', {
  type: 'draw',
  draw(gfx: Graphics, radius: number): void {
    // Outer silhouette only: top curve → right wall → bottom curve → left wall
    // Reads as a leaning cylinder/cannon at a glance, fills ~radius in all directions
    const halfH = radius * 0.84;     // vertical half-span (top to center)
    const lean = radius * 0.18;      // horizontal offset between top and bottom (creates tilt)
    const ellA = radius * 0.50;      // ellipse semi-major (horizontal width)
    const ellB = radius * 0.16;      // ellipse semi-minor (curve depth at top/bottom)
    const segs = 16;

    // Upper ellipse center (shifted right), lower ellipse center (shifted left)
    const ux = lean, uy = -halfH;
    const lx = -lean, ly = halfH;

    // Start at left edge of upper ellipse
    gfx.moveTo(ux - ellA, uy);

    // Upper arc: left → top → right (curves upward into negative Y)
    for (let i = 1; i <= segs; i++) {
      const a = Math.PI + (i / segs) * Math.PI; // PI → 2*PI
      gfx.lineTo(ux + Math.cos(a) * ellA, uy + Math.sin(a) * ellB);
    }

    // Right wall: straight line down to right edge of lower ellipse
    gfx.lineTo(lx + ellA, ly);

    // Lower arc: right → bottom → left (curves downward into positive Y)
    for (let i = 1; i <= segs; i++) {
      const a = (i / segs) * Math.PI; // 0 → PI
      gfx.lineTo(lx + Math.cos(a) * ellA, ly + Math.sin(a) * ellB);
    }

    // Left wall back up to start
    gfx.closePath();
  },
});

// ─── Gyro — teardrop (point faces DOWN, bulge on top) ───────────────────────

registerShape('gyro', {
  type: 'draw',
  draw(gfx: Graphics, radius: number): void {
    // Teardrop: circular bulge on top, taper to point on bottom
    const bulgeR = radius * 0.7;
    const taperY = radius;  // point faces down

    // Start from bottom point, curve up to bulge, around, back down
    gfx.moveTo(0, taperY);
    // Right curve from taper point to bulge
    gfx.quadraticCurveTo(bulgeR * 1.2, taperY * 0.2, bulgeR, -bulgeR * 0.4);
    // Top arc (semicircle approximation using two quad curves)
    gfx.quadraticCurveTo(bulgeR * 1.05, -bulgeR * 1.1, 0, -bulgeR * 1.1);
    gfx.quadraticCurveTo(-bulgeR * 1.05, -bulgeR * 1.1, -bulgeR, -bulgeR * 0.4);
    // Left curve back to taper point
    gfx.quadraticCurveTo(-bulgeR * 1.2, taperY * 0.2, 0, taperY);
  },
});

// ─── Vortex — wireframe hub + 3 spiral arm spines ──────────────────────────

registerShape('vortex', {
  type: 'draw',
  // draw() is unused for vortex — handled by drawVortexWireframe() directly
  draw(_gfx: Graphics, _radius: number): void {
    // no-op: vortex wireframe is special-cased in drawTankShape/strokeTankShape
  },
});

// ─── Shooter — perfect 5-point star ─────────────────────────────────────────

registerShape('shooter', {
  type: 'polygon',
  vertices(radius: number): number[] {
    const pts: number[] = [];
    const spikes = 5;
    const innerMult = 0.45;  // inner notch depth
    for (let i = 0; i < spikes; i++) {
      // Outer point
      const outerAngle = (i * Math.PI * 2) / spikes - Math.PI / 2;
      pts.push(Math.cos(outerAngle) * radius, Math.sin(outerAngle) * radius);
      // Inner notch
      const innerAngle = ((i + 0.5) * Math.PI * 2) / spikes - Math.PI / 2;
      pts.push(Math.cos(innerAngle) * radius * innerMult, Math.sin(innerAngle) * radius * innerMult);
    }
    return pts;
  },
});

// ─── Orbital — small diamond core (same pattern as Bouncer, smaller) ────────

registerShape('orbital', {
  type: 'polygon',
  vertices(radius: number): number[] {
    return [
      0, -radius,        // top
      radius, 0,         // right
      0, radius,         // bottom
      -radius, 0,        // left
    ];
  },
});

// ─── Cupid — heart shape (bezier curves, point down) ────────────────────────

registerShape('cupid', {
  type: 'draw',
  draw(gfx: Graphics, radius: number): void {
    // Heart: full round lobes, clean center dip, sharp bottom tip
    const r = radius;

    gfx.moveTo(0, r); // bottom tip

    // Left side: tip sweeps wide into left lobe
    gfx.bezierCurveTo(
      -r * 0.35, r * 0.35,    // pulls curve left near bottom
      -r * 1.1, -r * 0.05,    // wide sweep at mid-height
      -r * 0.55, -r * 0.6,    // left lobe peak
    );
    // Left lobe dome curves into center dip
    gfx.bezierCurveTo(
      -r * 0.2, -r * 0.85,    // above lobe peak — rounds the dome
      0, -r * 0.45,            // approaches dip from above
      0, -r * 0.3,             // center dip
    );

    // Right side: center dip curves into right lobe dome
    gfx.bezierCurveTo(
      0, -r * 0.45,            // leaves dip upward
      r * 0.2, -r * 0.85,     // above lobe peak — rounds the dome
      r * 0.55, -r * 0.6,     // right lobe peak
    );
    // Right lobe sweeps wide back to bottom tip
    gfx.bezierCurveTo(
      r * 1.1, -r * 0.05,     // wide sweep at mid-height
      r * 0.35, r * 0.35,     // pulls curve right near bottom
      0, r,                    // bottom tip
    );
  },
});

// ─── Vortex Wireframe ────────────────────────────────────────────────────────

function drawVortexWireframe(gfx: Graphics, radius: number, color: number, alphaScale: number = 1.0): void {
  const coreR = radius * 0.65;

  // Hub circle — 3-pass neon stroke
  for (const pass of BODY_PASSES) {
    gfx.circle(0, 0, coreR * 0.1);
    gfx.stroke({ color: lighten(color, pass.colorShift), width: pass.width, alpha: pass.alpha * alphaScale });
  }

  // 3 arm spines — open polylines along the spiral center trace
  const arms = 3;
  const steps = 30;
  const maxAngle = Math.PI * 1.8;

  for (let arm = 0; arm < arms; arm++) {
    const armOffset = (arm * Math.PI * 2) / arms;

    for (const pass of BODY_PASSES) {
      gfx.moveTo(
        Math.cos(armOffset) * (coreR * 0.8),
        Math.sin(armOffset) * (coreR * 0.8),
      );
      for (let i = 1; i <= steps; i++) {
        const t = i / steps;
        const angle = armOffset + t * maxAngle;
        const r = coreR * 0.8 + t * (radius * 0.95 - coreR * 0.8);
        gfx.lineTo(Math.cos(angle) * r, Math.sin(angle) * r);
      }
      gfx.stroke({ color: lighten(color, pass.colorShift), width: pass.width, alpha: pass.alpha * alphaScale });
    }
  }
}

// ─── Public API ──────────────────────────────────────────────────────────────

/** Get the shape definition for a tank. Falls back to hexagon (Terraformer) */
export function getTankShapeDef(tankId: string): TankShapeDef {
  return SHAPE_REGISTRY[tankId] ?? SHAPE_REGISTRY['terraformer'];
}

/**
 * Universal entry point — draws a tank's shape as 3-pass neon wireframe strokes.
 * Graphics is drawn centered at (0,0) — caller positions via container.
 */
export function drawTankShape(gfx: Graphics, tankId: string, radius: number, color: number): void {
  const def = getTankShapeDef(tankId);

  // Vortex: special wireframe handler
  if (tankId === 'vortex') {
    drawVortexWireframe(gfx, radius, color);
    return;
  }

  if (def.type === 'polygon' && def.vertices) {
    const verts = def.vertices(radius);
    for (const pass of BODY_PASSES) {
      gfx.poly(verts);
      gfx.stroke({ color: lighten(color, pass.colorShift), width: pass.width, alpha: pass.alpha });
    }
  } else if (def.type === 'draw' && def.draw) {
    for (const pass of BODY_PASSES) {
      def.draw(gfx, radius);
      gfx.stroke({ color: lighten(color, pass.colorShift), width: pass.width, alpha: pass.alpha });
    }
  }
}

/**
 * Draw a tank shape as a 3-pass neon stroke outline (for ghost previews).
 * `alpha` acts as a global multiplier on all 3 passes.
 */
export function strokeTankShape(gfx: Graphics, tankId: string, radius: number, color: number, _width: number, alpha: number): void {
  const def = getTankShapeDef(tankId);

  // Vortex: special wireframe handler
  if (tankId === 'vortex') {
    drawVortexWireframe(gfx, radius, color, alpha);
    return;
  }

  if (def.type === 'polygon' && def.vertices) {
    const verts = def.vertices(radius);
    for (const pass of BODY_PASSES) {
      gfx.poly(verts);
      gfx.stroke({ color: lighten(color, pass.colorShift), width: pass.width, alpha: pass.alpha * alpha });
    }
  } else if (def.type === 'draw' && def.draw) {
    for (const pass of BODY_PASSES) {
      def.draw(gfx, radius);
      gfx.stroke({ color: lighten(color, pass.colorShift), width: pass.width, alpha: pass.alpha * alpha });
    }
  }
}
